import React from 'react';
import { observer } from 'owa-mobx-react';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isCapabilityEnabled } from 'owa-capabilities';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import { makeStyles, mergeClasses } from '@fluentui/react-components';

/**
 * Root element for the Ribbon that conditionally applies different styles based on:
 * - Fluent UI v9 flight: `mon-ribbon-fluent-v9-ribbon`
 * - Reading Pane Denseless flight: `rp-denseless`
 * - theme with bleed-through capability
 * - being in a Dialog
 * - being in a Popout
 */
export const RibbonRoot: React.FC<{
    children: React.ReactNode;
    isInDialog?: boolean;
}> = observer(({ children, isInDialog }) => {
    const isFluentV9Enabled = isFeatureEnabled('mon-ribbon-fluent-v9-ribbon');
    const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);

    const { root, rootV9, rootV9BleedThrough, denselessPopout, rootDenseless } = useStyles();

    return (
        <div
            id="RibbonRoot"
            className={mergeClasses(
                root,
                isFluentV9Enabled && !isInDialog && rootV9,
                isFluentV9Enabled && !isInDialog && isBleedThroughEnabled && rootV9BleedThrough,
                isFluentV9Enabled &&
                    !isBleedThroughEnabled &&
                    isRPDenselessEnabled &&
                    !isInDialog &&
                    rootDenseless,
                isFluentV9Enabled && isRPDenselessEnabled && isInDialog && denselessPopout
            )}
        >
            {children}
        </div>
    );
}, 'RibbonRoot');

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    rootV9: {
        boxSizing: 'border-box',
        // Ideally, this should be `0 16px` for correct spacing on the left and right edges of Ribbon.
        // For now, however, we need to slightly increase to 19px to line up with other modules.
        padding: '0 19px 0 16px',
        width: '100%',
    },
    rootV9BleedThrough: {
        padding: '0 4px',
    },
    rootDenseless: {
        padding: '0 0 0 8px',
        width: '100%',
    },
    denselessPopout: {
        padding: '0 18px',
        width: '100%',
    },
});
